import { useState, useEffect, useRef } from 'react'
;// 增强版 TypewriterEffect 组件
import Markdown from "@/components/Markdown";
import { flushSync } from 'react-dom';

interface TypewriterEffectProps {
  content: string;
  speed?: number;
  blinkCursor?: boolean;
  textStyle?: React.CSSProperties;
  onContentChange?: () => void;
  onStart?: () => void;
  onComplete?: () => void;
  scrollContainerRef?: React.RefObject<HTMLElement | null>; // 滚动容器引用
  autoScrollThreshold?: number; // 自动滚动阈值，默认50px
  streamingMode?: 'word' | 'sentence'; // 流式输出模式：单词或句子
}

const TypewriterEffect = ({ 
  content, 
  speed = 150, // 调整速度为单词显示的默认速度
  textStyle,
  onContentChange,
  onStart,
  onComplete,
  scrollContainerRef,
  autoScrollThreshold = 50,
  streamingMode = 'word' // 默认为单词模式
}: TypewriterEffectProps) => {
  const [displayedContent, setDisplayedContent] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const index = useRef(0);
  const unitsRef = useRef<string[]>([]); // 存储单词或句子
  
  // 检查用户是否在底部附近（没有主动向上滚动）
  const isNearBottom = () => {
    if (!scrollContainerRef?.current) {
      // 如果没有提供滚动容器引用，使用 window 滚动
      const scrollTop = window.scrollY || window.pageYOffset;
      const docEl = document.documentElement;
      if (!docEl) {
        console.warn('[TypewriterEffect] isNearBottom skipped: documentElement missing');
        return true;
      }
      const scrollHeight = docEl.scrollHeight;
      const clientHeight = window.innerHeight;
      
      // 如果距离底部小于阈值，认为用户没有主动向上滚动
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      return distanceFromBottom <= autoScrollThreshold;
    }
    
    const container = scrollContainerRef.current;
    if (!container.isConnected) {
      console.warn('[TypewriterEffect] isNearBottom skipped: scroll container disconnected');
      return true;
    }
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // 如果距离底部小于阈值，认为用户没有主动向上滚动
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    return distanceFromBottom <= autoScrollThreshold;
  };
  
  // 将内容分割为单词或句子
  const splitContent = (text: string): string[] => {
    if (streamingMode === 'sentence') {
      // 按句子分割：句号、问号、感叹号、分号
      // 保留分隔符
      const sentences = text.match(/[^.!?;]+[.!?;]+|[^.!?;]+$/g) || [];
      return sentences.map(s => s.trim()).filter(Boolean);
    } else {
      // 按单词分割
      return text.split(" ").filter(Boolean);
    }
  };
  
  // 内容变化时重置
  useEffect(() => {
    // Skip resetting if content is empty
    if (!content || content.trim() === '') {
      setIsComplete(true);
      return;
    }
    
    setDisplayedContent('');
    index.current = 0;
    setIsComplete(false);
    
    if (content?.length > 0) {
      unitsRef.current = splitContent(content);
      onStart && onStart();  
    }
  }, [content, streamingMode]);
  
  // 打字效果 - 根据模式以单词或句子为单位
  useEffect(() => {    
    // Handle empty content case
    if (!content || content.trim() === '') {
      setIsComplete(true);
      if (onComplete) {
        onComplete();
      }
      return;
    }
    
    if (index.current < unitsRef.current.length) {
      const timeout = setTimeout(() => {
        // 添加当前单元（单词或句子）
        flushSync(() => {
          setDisplayedContent(prev => {
            if (streamingMode === 'sentence') {
              // 句子模式：直接连接，因为句子已包含标点
              return prev + (prev ? ' ' : '') + unitsRef.current[index.current];
            } else {
              // 单词模式：添加空格
              return prev + (prev ? ' ' : '') + unitsRef.current[index.current];
            }
          });
        });
        index.current += 1;
        // 只有当用户在底部附近时才自动滚动
        if (onContentChange && isNearBottom()) {
          setTimeout(() => {
            onContentChange();
          }, 0);
        }
      }, speed);
      
      return () => clearTimeout(timeout);
    } else {
      setIsComplete(true);
      // 最后一次输出后，只有当用户在底部附近时才滚动
      if (onContentChange && isNearBottom()) {
        setTimeout(() => {
          onContentChange();
        }, 0);
      }
      // Call onComplete when typing is finished
      if (onComplete) {
        onComplete();
      }
    }
  // 依赖项去掉 onContentChange，避免多余 effect 触发
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, displayedContent, speed, streamingMode]);
  
  return (
    <div className="w-full" onScroll={() => {
      console.log('Typer scroll');
    }}>
      {isComplete ? (
        <Markdown content={content} textStyle={textStyle} />
      ) : (
        <>
          <Markdown content={displayedContent} textStyle={textStyle} />
        </>
      )}
    </div>
  );
};

export default TypewriterEffect;
